import { useMemo, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CONTRACT_TYPE_CLIENT, CONTRACT_TYPE_SUPPLIER, ContractType, IPagingData, LegalEntityBaseInfoDto } from '@sr/dto';
import { useSnack } from 'shared/ui/snack';
import { ContractsGrid } from 'components/Contracts/contracts.grid';
import { AddContractDialog } from 'widgets/contracts/add-dialog/add-contract-dialog';
import { FormValues } from 'components/Contracts/contract.form';
import { useOurCompany } from 'hooks/portal-settings.hook';
import { useContractsList } from 'entities/contract/lib';
import { DEFAULT_PAGINATION } from 'shared/ui/grid-layout';
import { CompanyContractsFilter } from 'features/contract/company-contracts-filter';
import { useSearchParams } from 'react-router-dom';

type Props = {
  companyId: number;
  contractType?: ContractType;
};

const CONTRACT_FILTER_KEYS = ['sellerLegalEntityId', 'buyerLegalEntityId', 'managerId', 'fromDate', 'toDate', 'createdAtFrom', 'createdAtTo'] as const;

const buildContractsFilterString = (
  contractType: ContractType,
  searchParams: URLSearchParams,
  queryParamPrefix: string
) => {
  const params = new URLSearchParams();
  params.set('type', String(contractType));

  const setIfPresent = (key: (typeof CONTRACT_FILTER_KEYS)[number]) => {
    const value = searchParams.get(`${queryParamPrefix}${key}`);
    if (value) params.set(key, value);
  };

  CONTRACT_FILTER_KEYS.forEach(setIfPresent);

  return params.toString();
};

export const CompanyContracts = ({ companyId, contractType = CONTRACT_TYPE_CLIENT }: Props) => {
  const [paging, setPaging] = useState<IPagingData>(DEFAULT_PAGINATION);
  const [searchParams] = useSearchParams();
  const queryParamPrefix = contractType === CONTRACT_TYPE_SUPPLIER ? 'supplierContracts_' : 'clientContracts_';
  const filterString = useMemo(() => {
    return buildContractsFilterString(contractType, searchParams, queryParamPrefix);
  }, [contractType, queryParamPrefix, searchParams]);

  const { collection, addContract } = useContractsList(paging, filterString, companyId);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const ourCompanyId = useOurCompany();

  const { showSuccess, showError } = useSnack();
  const handleAddContract = async (data: FormValues) => {
    return addContract(data, companyId)
      .then(() => {
        setCreateDialogOpen(false);
        showSuccess('Договор успешно добавлен');
        setPaging({ ...paging });
      })
      .catch((e) => showError(`Ошибка добавления договора: ${e.message}`));
  };
  const handleDelete = async (entity: LegalEntityBaseInfoDto) => {};

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="h6" color="grey.700">
          {contractType === CONTRACT_TYPE_SUPPLIER ? 'Договоры поставщиков' : 'Клиентские договоры'}
        </Typography>
        <Button
          size={'small'}
          sx={{ mb: 2 }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setCreateDialogOpen(true);
          }}
        >
          Добавить
        </Button>
      </Stack>

      <CompanyContractsFilter companyId={companyId} contractType={contractType} queryParamPrefix={queryParamPrefix} />
      <ContractsGrid contracts={collection} paging={paging} onChangePaging={setPaging} contractType={contractType} />

      <AddContractDialog
        buyerCompanyId={companyId}
        ourCompanyId={ourCompanyId}
        contractType={contractType}
        open={createDialogOpen}
        title={Number(contractType) === CONTRACT_TYPE_CLIENT ? 'Добавить клиентский договор' : 'Добавить договор поставщика'}
        onClose={() => setCreateDialogOpen(false)}
        submit={handleAddContract}
      />
    </>
  );
};
